'use client';

import { useEffect } from 'react';

/**
 * WebMCP: the page offering its own actions to a browser agent as callable tools. An origin-trial
 * API, so `document.modelContext` is undefined for almost every visitor and this is a no-op by
 * design. See the WebMCP entry in this app's CLAUDE.md.
 */
export type WebMcpTool = {
  name: string;
  description: string;
  /** JSON Schema. `webmcp-schema-validity` fails the page if this does not describe the input. */
  inputSchema: {
    type: 'object';
    properties: Record<string, { type: string; description: string; enum?: string[] }>;
    required?: string[];
  };
  /** `input` is checked against `inputSchema` by the caller, not by TypeScript. */
  execute: (input: never, options: { signal: AbortSignal }) => Promise<string>;
  annotations?: { readOnlyHint?: boolean };
};

declare global {
  interface Document {
    modelContext?: {
      registerTool: (tool: WebMcpTool, options: { signal: AbortSignal }) => Promise<void>;
    };
  }
}

/**
 * Offers a tool while the component offering it is mounted. Aborting is idempotent, so Strict
 * Mode's double-invoked effect cannot leave a duplicate registered.
 */
export function useWebMcpTool(tool: WebMcpTool) {
  useEffect(() => {
    if (!document.modelContext) return;
    const controller = new AbortController();
    // Rejects if the page is torn down before registration resolves, which is not worth reporting.
    void document.modelContext.registerTool(tool, { signal: controller.signal }).catch(() => {});
    return () => controller.abort();
  }, [tool]);
}
